import { Link, useParams } from "react-router";

import { EnlaceVolver } from "@/components/EnlaceVolver";
import { ConfirmacionDeEnvio } from "@/components/informes/ConfirmacionDeEnvio";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useInforme } from "@/hooks/useInformes";

/**
 * "Cotización enviada a revisión" (RF-F3, pantalla `19 UN`).
 *
 * Era un estado local del formulario de cotización, así que existía solo en el
 * instante posterior al envío: cualquier navegación la borraba, y volver desde
 * el documento caía en el formulario. Ahora es una pantalla con URL propia, que
 * se redibuja desde el informe persistido — por eso la ficha del backend
 * incluye los `revisores` de la cotización vigente.
 */
export function CotizacionEnviada() {
  const { id } = useParams();
  const informeId = Number(id);
  const ficha = useInforme(informeId);

  if (ficha.isPending) {
    return (
      <div className="space-y-5 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full max-w-[70ch] rounded-xl" />
      </div>
    );
  }

  if (ficha.isError || !ficha.data) {
    return (
      <div className="space-y-4 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
        <EnlaceVolver to="/informes">Volver a informes en proceso</EnlaceVolver>
        <p className="text-destructive text-sm">
          No pudimos cargar este informe. Puede que ya no exista o que la conexión haya fallado.
        </p>
      </div>
    );
  }

  const informe = ficha.data;
  const cotizacion = informe.cotizacion ?? null;

  return (
    <div className="space-y-5 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      <EnlaceVolver to="/informes">Volver a informes en proceso</EnlaceVolver>

      {cotizacion ? (
        <ConfirmacionDeEnvio informe={informe} cotizacion={cotizacion} />
      ) : (
        // La URL se puede abrir a mano, o el informe pudo cambiar en otra
        // pestaña. Afirmar "enviada a revisión" sin cotización sería falso.
        <div className="max-w-[70ch] space-y-4">
          <p className="text-ink-muted text-caption">
            Este informe todavía no tiene una cotización enviada a revisión.
          </p>
          <Button asChild>
            <Link to={`/informes/${informe.id}/cotizacion`}>Cotizar el informe</Link>
          </Button>
        </div>
      )}
    </div>
  );
}
